/**
 * Facts a title widget can show instead of fixed text (AgencyAnalytics'
 * "dynamic variables"): the client's name and website, the report's name
 * and period, the agency's name and the page's title. Whoever renders a page
 * provides what it knows; anything unknown shows as a labelled placeholder.
 */

import { createContext, useContext } from 'react';

import type { WidgetDisplay } from './reports-api.ts';

export interface ReportFacts {
  clientName?: string | undefined;
  clientUrl?: string | null | undefined;
  reportName?: string | undefined;
  periodLabel?: string | undefined;
  agencyName?: string | undefined;
  pageTitle?: string | undefined;
}

export const TITLE_SOURCES = [
  { key: 'text', label: 'Text you type' },
  { key: 'client_name', label: 'Client name' },
  { key: 'client_url', label: 'Client website' },
  { key: 'report_name', label: 'Report name' },
  { key: 'report_date', label: 'Report date range' },
  { key: 'agency_name', label: 'Agency name' },
  { key: 'page_title', label: 'Page title' },
] as const;

export type TitleSource = (typeof TITLE_SOURCES)[number]['key'];

export const ReportFactsContext = createContext<ReportFacts>({});
export const useReportFacts = (): ReportFacts => useContext(ReportFactsContext);

const FACT: Record<Exclude<TitleSource, 'text'>, keyof ReportFacts> = {
  client_name: 'clientName',
  client_url: 'clientUrl',
  report_name: 'reportName',
  report_date: 'periodLabel',
  agency_name: 'agencyName',
  page_title: 'pageTitle',
};

/** The words a title widget shows; for an unknown fact, a labelled placeholder (agency) or nothing (client). */
export function titleText(display: WidgetDisplay, facts: ReportFacts, forClient = false): string {
  const source = (display.title_source ?? 'text') as TitleSource;
  if (source === 'text') return display.title || 'Section';
  const value = facts[FACT[source]];
  if (typeof value === 'string' && value) return value;
  return forClient ? '' : `[${TITLE_SOURCES.find((item) => item.key === source)?.label}]`;
}
